import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { History, Plus, Pencil, Trash2, UserPlus, Mail, Building2, RotateCcw } from "lucide-react";

const ACTION_META = {
  create: { label: "Création", color: "#10b981", Icon: Plus },
  update: { label: "Modification", color: "#f59e0b", Icon: Pencil },
  delete: { label: "Suppression", color: "#ef4444", Icon: Trash2 },
  bulk_create: { label: "Création en masse", color: "#10b981", Icon: Plus },
  rename_floor: { label: "Étage renommé", color: "#f59e0b", Icon: Pencil },
  delete_floor: { label: "Étage supprimé", color: "#ef4444", Icon: Building2 },
  reset_maison: { label: "Maison réinitialisée", color: "#f59e0b", Icon: RotateCcw },
  create_user: { label: "Nouvel utilisateur", color: "#8b5cf6", Icon: UserPlus },
  create_maison: { label: "Nouvelle maison", color: "#06b6d4", Icon: Building2 },
  rename_maison: { label: "Maison renommée", color: "#f59e0b", Icon: Pencil },
  delete_maison: { label: "Maison supprimée", color: "#ef4444", Icon: Building2 },
  send_reminders: { label: "Rappels e-mail", color: "#ec4899", Icon: Mail },
  send_order_email: { label: "Bon de commande envoyé", color: "#ec4899", Icon: Mail },
};

const COLL_LABELS = {
  tasks: "Tâche", residents: "Résident", families: "Famille", prospects: "Prospect",
  hr: "RH", invoices: "Facture", courriers: "Courrier", quality: "Qualité", events: "Agenda",
  users: "Utilisateur", maisons: "Maison", reminders: "Rappels", formations: "Formation",
  crm_prospects: "Prospect", crm_rooms: "Chambre", crm_visits: "Visite",
  crm_admissions: "Admission", crm_partners: "Partenaire", crm_followups: "Relance",
  crm_checklist: "Checklist visite", crm_doc_checklist: "Checklist documents",
};

export default function Audit() {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const load = () => api.get("/audit").then((r) => setLogs(r.data));
  useEffect(() => { load(); }, []);

  const clearAll = async () => {
    if (!window.confirm("Vider TOUT l'historique des actions (admin, directeurs, adjoints, CEO, attachés) ? Cette action est irréversible.")) return;
    try { const r = await api.delete("/audit"); toast.success(`Historique vidé (${r.data.deleted || 0} entrées).`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="p-5 sm:p-8 max-w-[1100px] mx-auto">
      <div data-tour="audit-header" className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-sky-300">Administration</p>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mt-1 flex items-center gap-3">
            <span className="h-11 w-11 rounded-2xl flex items-center justify-center bg-sky-500/20 text-sky-400"><History className="h-6 w-6" /></span>
            Historique des actions
          </h1>
          <p className="text-muted-foreground mt-1">Traçabilité complète : qui a fait quoi, et quand.</p>
        </div>
        {user?.role === "admin" && (
          <button onClick={clearAll} data-testid="audit-clear-all"
            className="mt-2 inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold border border-rose-500/30 text-rose-300 hover:bg-rose-500/15 transition">
            <Trash2 className="h-4 w-4" /> Vider l'historique
          </button>
        )}
      </div>

      <div className="glass rounded-2xl card-3d mt-6 divide-y divide-white/5" data-tour="audit-list">
        {logs.length === 0 && <p className="text-muted-foreground text-center py-12">Aucune action enregistrée.</p>}
        {logs.map((l) => {
          const meta = ACTION_META[l.action] || { label: l.action, color: "#94a3b8", Icon: History };
          const Icon = meta.Icon;
          return (
            <div key={l.id} className="flex items-center gap-4 px-5 py-3.5" data-testid={`audit-${l.id}`}>
              <span className="h-9 w-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: meta.color + "22", color: meta.color }}><Icon className="h-4 w-4" /></span>
              <div className="min-w-0 flex-1">
                <p className="text-sm">
                  <span className="font-semibold">{l.user_name}</span>
                  <span className="text-muted-foreground"> · {meta.label}</span>
                  {COLL_LABELS[l.target] && <span className="text-muted-foreground"> · {COLL_LABELS[l.target]}</span>}
                  {l.detail && <span className="text-foreground/80"> — {l.detail}</span>}
                </p>
                <p className="text-xs text-muted-foreground">{l.maison}</p>
              </div>
              <span className="text-xs text-muted-foreground font-mono shrink-0">{new Date(l.timestamp).toLocaleString("fr-BE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
